import React, { FC, useCallback, useState } from "react";
import { HomeHeaderWithContext } from "../HomeHeader";
import DividerWithText from "../DividerWithText";
import { Box, Button, Grid, TextField } from "@mui/material";
import { Accounts } from "../../api/portal";
import { AccountSelf } from "../../types/account";
import useAuthentication from "../../hooks/useAuthentication";
import { AxiosError } from "axios";
import { useSnackbar } from "notistack";

const FORM_WIDTH = 250;

export type EditAccountProps = {
  /**
   * Callback function that gets executed when
   *   1. The account data are successfully updated
   *   2. The cancel button is pressed
   */
  onFinish: () => void;
};

/**
 * Form for editing one's own account data.
 */
const EditAccount: FC<EditAccountProps> = ({ onFinish }) => {
  const { user, setAuthenticationContextValue } = useAuthentication();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [userData, setUserData] = useState<AccountSelf>(user as AccountSelf);

  const handleUserDataChange = useCallback(
    (key: keyof AccountSelf, value: AccountSelf[typeof key]) => {
      setUserData((currentAccountData) => {
        return { ...currentAccountData, [key]: value };
      });
    },
    []
  );

  return (
    <>
      <HomeHeaderWithContext />
      <Grid item width={FORM_WIDTH * 1.5}>
        <DividerWithText>Accountdaten</DividerWithText>
      </Grid>
      <Grid
        container
        width={FORM_WIDTH * 1.5}
        flexDirection="column"
        alignItems="center"
      >
        <Grid item>
          <Grid container spacing={1}>
            <Grid item sm={5.5}>
              <TextField
                margin="normal"
                title="Vorname"
                fullWidth
                id="vorname"
                label="Vorname"
                size="small"
                name="vorname"
                disabled={!user?.admin}
                value={userData.vorname}
                onChange={(e) =>
                  handleUserDataChange("vorname", e.target.value)
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item sm={6.5}>
              <TextField
                margin="normal"
                title="Nachname"
                fullWidth
                id="nachname"
                label="Nachname"
                size="small"
                name="nachname"
                InputLabelProps={{ shrink: true }}
                value={userData.nachname}
                onChange={(e) =>
                  handleUserDataChange("nachname", e.target.value)
                }
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item>
          <TextField
            style={{ width: FORM_WIDTH * 1.5 }}
            sx={{ mt: 0.5 }}
            margin="normal"
            title="Position"
            id="position"
            label="Position"
            size="small"
            name="position"
            InputLabelProps={{ shrink: true }}
            value={userData.position}
            onChange={(e) => handleUserDataChange("position", e.target.value)}
          />
        </Grid>
        <Grid item>
          <TextField
            style={{ width: FORM_WIDTH * 1.5 }}
            sx={{ mt: 0.5 }}
            margin="normal"
            title="Institut"
            id="institut"
            label="Institut"
            size="small"
            InputLabelProps={{ shrink: true }}
            name="institut"
            value={userData.institut}
            onChange={(e) => handleUserDataChange("institut", e.target.value)}
          />
        </Grid>
        <Grid item>
          <Grid container spacing={1}>
            <Grid item sm={4}>
              <TextField
                margin="normal"
                sx={{ mt: 0.5 }}
                title="PLZ"
                fullWidth
                id="plz"
                label="Postleitzahl"
                InputLabelProps={{ shrink: true }}
                size="small"
                name="plz"
                value={userData.plz}
                onChange={(e) => handleUserDataChange("plz", e.target.value)}
              />
            </Grid>
            <Grid item sm={8}>
              <TextField
                margin="normal"
                title="Ort"
                sx={{ mt: 0.5 }}
                fullWidth
                id="ort"
                label="Ort"
                size="small"
                name="ort"
                InputLabelProps={{ shrink: true }}
                value={userData.ort}
                onChange={(e) => handleUserDataChange("ort", e.target.value)}
              />
            </Grid>
          </Grid>
          <Grid item>
            <TextField
              style={{ width: FORM_WIDTH * 1.5 }}
              sx={{ mt: 0.5 }}
              margin="normal"
              title="Ortsteil"
              id="ortsteil"
              label="Ortsteil"
              size="small"
              name="ortsteil"
              value={userData.ortz}
              InputLabelProps={{ shrink: true }}
              onChange={(e) => handleUserDataChange("ortz", e.target.value)}
            />
          </Grid>
          <Grid item>
            <TextField
              style={{ width: FORM_WIDTH * 1.5 }}
              sx={{ mt: 0.5 }}
              margin="normal"
              title="Email"
              id="email"
              label="Email"
              size="small"
              name="email"
              InputLabelProps={{ shrink: true }}
              value={userData.email}
              onChange={(e) => handleUserDataChange("email", e.target.value)}
            />
          </Grid>
          <Grid item>
            <TextField
              style={{ width: FORM_WIDTH * 1.5 }}
              sx={{ mt: 0.5 }}
              margin="normal"
              title="Mobil"
              id="mobil"
              label="Mobil"
              InputLabelProps={{ shrink: true }}
              size="small"
              name="mobil"
              value={userData.mobil}
              onChange={(e) => handleUserDataChange("mobil", e.target.value)}
            />
          </Grid>
          <Grid item>
            <TextField
              style={{ width: FORM_WIDTH * 1.5 }}
              sx={{ mt: 0.5 }}
              margin="normal"
              title="Telefon"
              id="telefon"
              label="Telefon"
              InputLabelProps={{ shrink: true }}
              size="small"
              name="telefon"
              value={userData.telefon}
              onChange={(e) => handleUserDataChange("telefon", e.target.value)}
            />
          </Grid>
          <Grid item>
            <Box
              display="flex"
              justifyContent="center"
              gap={2}
              sx={{ mt: 3, mb: 10 }}
            >
              <Button
                variant="contained"
                disabled={loading}
                onClick={() => {
                  setLoading(true);
                  Accounts.patchMe(userData)
                    .then(() => {
                      enqueueSnackbar("Accountdaten erfolgreich geändert");

                      // Update context value so that there is no need to
                      // refresh the page to see updates in other components
                      setAuthenticationContextValue((currContextVal) => {
                        return {
                          ...currContextVal,
                          user: {
                            ...currContextVal.user,
                            ...userData,
                          },
                        };
                      });
                      onFinish();
                    })
                    .catch((err: AxiosError) => {
                      enqueueSnackbar(err.response?.data || err.message);
                    })
                    .finally(() => {
                      setLoading(false);
                    });
                }}
              >
                Speichern
              </Button>
              <Button
                onClick={() => {
                  onFinish();
                }}
              >
                Abbrechen
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default EditAccount;
